import { useCallback, useContext, useMemo, useState } from 'react';
import { Visibility, Description, Map } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { Table, Pagination } from '../../../components';
import { IResponse, ISortDirection, IBuild } from '../../../models';
import { downloadContract, downloadSitePlan, getPersonBuilds } from '../../../fetch';
import { Button, Box, useMediaQuery } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { UserContext } from '../../../context';
import { useSnackbar } from 'notistack';
import { MobileMyBuilds } from './MobileMyBuilds';

export const MyBuildList = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  // if the purchaser/client user came from the homeowner portal, we will show the download button
  const queryParams = new URLSearchParams(useLocation().search);
  const viewHomeownerPortalDocs = Boolean(queryParams.get('homeownerPortal'));

  const { user } = useContext(UserContext);
  const personId = user?.externalUser?.personId;

  const isMobile = useMediaQuery('(max-width: 960px)');

  const [selectedSort, setSelectedSort] = useState<string>('Neighborhood');
  const [sortDirection, setSortDirection] = useState<{
    Neighborhood?: ISortDirection;
    LotNumber?: ISortDirection;
    Plan?: ISortDirection;
    Status?: ISortDirection;
  }>({
    Neighborhood: 'Asc'
  });

  const { isLoading, data: buildData } = useQuery<IResponse<IBuild[]>, Error>(
    ['getPersonBuilds', page, perPage, selectedSort, sortDirection],
    async () =>
      await getPersonBuilds(personId, {
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      }),
    {
      enabled: !!personId,
      onSuccess: data => {
        if (data.totalRecordCount === 1) {
          history.push(`/my-build/${data.records[0].buildId}`, { userHasOneBuild: true });
        }
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleView = useCallback(
    (build: IBuild) => {
      history.push(`/my-build/${build.buildId}`);
    },
    [history]
  );

  const handleSitePlanDownload = useCallback(async (build: IBuild) => {
    try {
      await downloadSitePlan(build.buildId);
    } catch (error) {
      enqueueSnackbar('Failed to download site plan', { variant: 'error' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContractDownload = useCallback(async (build: IBuild) => {
    try {
      await downloadContract(build.buildId);
    } catch (error) {
      enqueueSnackbar('Failed to download contract', { variant: 'error' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = useMemo(() => buildData?.records ?? [], [buildData]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodName',
        isServerSorted: selectedSort === 'Neighborhood',
        isServerSortedDesc: sortDirection.Neighborhood === 'Desc',
        handleClickColumn: () => handleClickColumn('Neighborhood')
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Plan',
        accessor: 'plan',
        isServerSorted: selectedSort === 'Plan',
        isServerSortedDesc: sortDirection.Plan === 'Desc',
        handleClickColumn: () => handleClickColumn('Plan')
      },
      {
        Header: 'Status',
        accessor: 'status',
        isServerSorted: selectedSort === 'Status',
        isServerSortedDesc: sortDirection.Status === 'Desc',
        handleClickColumn: () => handleClickColumn('Status')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuild } };
        }) => {
          return (
            <Box display="flex" gap={1} justifyContent="flex-end">
              {viewHomeownerPortalDocs && original.hasSitePlanFile && (
                <Button
                  id="download-site-plan"
                  type="button"
                  color="primary"
                  startIcon={<Map />}
                  onClick={() => {
                    handleSitePlanDownload(original);
                  }}
                >
                  View Site Plan
                </Button>
              )}
              {viewHomeownerPortalDocs && original.hasContractFile && (
                <Button
                  id="download-contract"
                  type="button"
                  color="secondary"
                  startIcon={<Description />}
                  onClick={() => {
                    handleContractDownload(original);
                  }}
                >
                  View Contract
                </Button>
              )}
              <Button
                id="view-build"
                type="button"
                color="primary"
                startIcon={<Visibility />}
                onClick={() => {
                  handleView(original);
                }}
              >
                View
              </Button>
            </Box>
          );
        }
      }
    ];
  }, [
    selectedSort,
    sortDirection,
    handleView,
    viewHomeownerPortalDocs,
    handleSitePlanDownload,
    handleContractDownload
  ]);
  return (
    <>
      <Table
        columns={columns}
        data={data}
        isLoading={isLoading}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({ name: c.Header, accessor: c.accessor, Cell: c?.Cell })),
          handleView: handleView,
          handleDownloadSitePlan: (val: IBuild) => handleSitePlanDownload(val),
          showSitePlan: (val: IBuild) => Boolean(viewHomeownerPortalDocs && val.hasSitePlanFile),
          handleDownloadContract: (val: IBuild) => handleContractDownload(val),
          showContract: (val: IBuild) => Boolean(viewHomeownerPortalDocs && val.hasContractFile)
        }}
        ResponsiveComponent={isMobile ? MobileMyBuilds : undefined}
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </>
  );
};
